import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useMeta } from "@/hooks/useMeta";
import { PageHeader, FilterCard, Field, SimpleSelect } from "@/components/common";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Target, Save, Zap, Loader2 } from "lucide-react";

export default function TargetTahunan() {
  const { indikators, tahuns } = useMeta();
  const [indikator, setIndikator] = useState("");
  const [tahun, setTahun] = useState(String(new Date().getFullYear()));
  const [polis, setPolis] = useState([]);
  const [targets, setTargets] = useState({});
  const [bulkVal, setBulkVal] = useState("80");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (!indikator && indikators.length) setIndikator(indikators[0].kode); }, [indikators]);

  const load = async () => {
    if (!indikator || !tahun) return;
    const p = await api.get(`/poliklinik?indikator=${encodeURIComponent(indikator)}`);
    const t = await api.get(`/target?tahun=${tahun}&indikator=${encodeURIComponent(indikator)}`);
    setPolis(p.data);
    const map = {};
    t.data.forEach((x) => { map[x.poliklinik_id] = Math.round(x.target * 100); });
    setTargets(map);
  };
  useEffect(() => { load(); }, [indikator, tahun]);

  const applyBulk = () => {
    const v = Number(bulkVal);
    if (isNaN(v)) return toast.error("Nilai tidak valid");
    const map = {};
    polis.forEach((p) => { map[p.id] = v; });
    setTargets(map);
    toast.success(`Target ${v}% diterapkan ke semua poliklinik (klik Simpan untuk menyimpan)`);
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      for (const p of polis) {
        const val = targets[p.id];
        if (val === undefined || val === "" || val === null) continue;
        await api.post("/target", {
          poliklinik_id: p.id, indikator, tahun: Number(tahun), target: Number(val) / 100,
        });
      }
      toast.success("Semua target tersimpan");
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const filledCount = polis.filter((p) => targets[p.id] !== undefined && targets[p.id] !== "").length;

  return (
    <div data-testid="target-page">
      <PageHeader icon={Target} title="Kelola Target Tahunan" subtitle="Target diisi 1x per tahun. Target boleh berbeda tiap tahun — sistem otomatis pakai target sesuai tahun data.">
        <Button onClick={saveAll} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="target-save-button">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Simpan Semua
        </Button>
      </PageHeader>

      <FilterCard>
        <Field label="Indikator">
          <SimpleSelect testId="target-indikator-select" value={indikator} onChange={setIndikator}
            options={indikators.map((i) => ({ label: i.kode, value: i.kode }))} className="w-[220px]" />
        </Field>
        <Field label="Tahun">
          <SimpleSelect testId="target-year-select" value={tahun} onChange={setTahun}
            options={[...new Set([...tahuns, Number(tahun)])].sort((a, b) => b - a).map((t) => ({ label: String(t), value: t }))} className="w-[120px]" />
        </Field>
        <Field label="Set semua sekaligus (%)">
          <div className="flex gap-2">
            <Input type="number" value={bulkVal} onChange={(e) => setBulkVal(e.target.value)} className="w-24 bg-white" data-testid="target-bulk-input" />
            <Button variant="outline" onClick={applyBulk} data-testid="target-bulk-apply"><Zap className="w-4 h-4 mr-2" /> Terapkan</Button>
          </div>
        </Field>
      </FilterCard>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg">Target {indikator} — Tahun {tahun}
            <Badge variant="secondary" className="ml-2">{filledCount}/{polis.length} terisi</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Departemen</TableHead>
                  <TableHead>Poliklinik</TableHead>
                  <TableHead className="w-[180px]">Target (%)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {polis.map((p) => (
                  <TableRow key={p.id} data-testid={`target-row-${p.id}`}>
                    <TableCell className="text-slate-600">{p.nama_departemen}</TableCell>
                    <TableCell className="font-medium">{p.nama_poliklinik}</TableCell>
                    <TableCell>
                      <Input type="number" min="0" max="100"
                        value={targets[p.id] ?? ""}
                        onChange={(e) => setTargets({ ...targets, [p.id]: e.target.value })}
                        placeholder="—"
                        className="w-28 tabular-nums"
                        data-testid={`target-input-${p.id}`} />
                    </TableCell>
                  </TableRow>
                ))}
                {polis.length === 0 && (
                  <TableRow><TableCell colSpan={3} className="text-center text-slate-400 py-8">Belum ada poliklinik untuk indikator ini</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
